import { useMemo, useState, useEffect } from 'react';
import { View, Text, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import SheetModal from '@/components/General/SheetModal';
import SelectInput from '@/components/General/inputs/SelectInput';
import { Query } from '@/utils/react-query/Queries';
import useTabConfigStore, { DEFAULT_TAB_KEYS, TAB_SLOT_KEYS } from '@/data/store/useTabConfigStore';
import { getTabOptionsForUser } from '@/utils/constants/tabOptions';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg, resolveEffectiveTabs } from '@/utils/organizationEntitlements';
import Unicon from '@/components/General/Unicon';

const SLOT_LABELS = ['First Tab', 'Second Tab', 'Third Tab', 'Fourth Tab'];

const TabConfigModal = ({ isOpen, onClose }) => {
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { entitlements } = useOrganizationEntitlements();
    const isFree = isFreeOrg(entitlements);
    // Small group types are Paid-only tab options — skip the query on Free.
    const { data: smallGroupTypes } = Query.smallGroupTypes.all({options: {enabled: !isFree}});
    const { data: organization } = Query.organization.byId({
        id: loggedInUser?.organizationID,
        options: { enabled: !isFree && !!loggedInUser?.organizationID }
    });
    const bibleStudiesContext = useMemo(() => ({
        enableBibleStudies: organization?.enableBibleStudies === true,
        bibleStudyType: (smallGroupTypes || []).find(t => t.purpose === 'BIBLE_STUDY' && t.roleID) || null,
    }), [organization?.enableBibleStudies, smallGroupTypes]);
    const customTabs = useTabConfigStore((state) => state.customTabs);
    const setCustomTabs = useTabConfigStore((state) => state.setCustomTabs);
    const resetToDefaults = useTabConfigStore((state) => state.resetToDefaults);

    const [draft, setDraft] = useState({ ...DEFAULT_TAB_KEYS });

    useEffect(() => {
        if (isOpen) {
            // Show the plan-effective selection — on Free, stored Paid picks
            // resolve to their allowed replacement instead of a hidden value.
            setDraft({ ...resolveEffectiveTabs(customTabs, entitlements, DEFAULT_TAB_KEYS) });
        }
    }, [isOpen, customTabs, entitlements]);

    const availableOptions = useMemo(() => getTabOptionsForUser(loggedInUser, smallGroupTypes, entitlements, bibleStudiesContext), [loggedInUser, smallGroupTypes, entitlements, bibleStudiesContext]);
    const selectOptions = useMemo(() => availableOptions.map((o) => ({ label: o.label, value: o.key })), [availableOptions]);

    const updateSlot = (slot, key) => {
        setDraft((prev) => ({ ...prev, [slot]: key }));
    };

    const handleSave = async () => {
        await setCustomTabs(draft);
        if (onClose) onClose();
    };

    const handleReset = async () => {
        await resetToDefaults();
        if (onClose) onClose();
    };

    return (
        <SheetModal
            title="Customize Bottom Tabs"
            isOpen={isOpen}
            onCancel={onClose}
            confirmText="Save"
            onConfirm={handleSave}
            centerTitle
        >
            <View style={styles.container}>
                <Text style={styles.description}>Choose what appears on each of the four bottom tabs.</Text>
                {TAB_SLOT_KEYS.map((slot, index) => (
                    <SelectInput
                        key={slot}
                        title={SLOT_LABELS[index]}
                        value={draft[slot]}
                        onUpdate={(value) => updateSlot(slot, value)}
                        options={selectOptions}
                    />
                ))}

                <Pressable testID="tab-config-reset" style={styles.resetButton} onPress={handleReset}>
                    <Unicon name="refresh" size={18} colorVariant="primary" />
                    <Text style={styles.resetText}>Reset to Defaults</Text>
                </Pressable>
            </View>
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, typography }) => ({
    container: {
        padding: spacing.md,
        paddingBottom: spacing.xl,
    },
    description: {
        fontFamily: fonts.body,
        fontSize: typography.body,
        color: colors.textSecondary,
        textAlign: 'center',
        marginBottom: spacing.md,
    },
    resetButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: spacing.sm,
        marginTop: spacing.md,
        paddingVertical: spacing.sm,
    },
    resetText: {
        fontFamily: fonts.heading,
        fontSize: typography.body,
        color: colors.primary,
    },
}));

export default TabConfigModal;
